import animation from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import MediaCardStyles from '@stories/Components/Cards/MediaCard/MediaCard.styles';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import ParagraphStyles from '@stories/Components/Global/Typography/Paragraph.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  overflow: hidden;
  --bgColor: ${brand.toast.toastLight};
  --headingColor: ${brand.sage.sageDark};
  --textColor: ${brand.sage.sageDark};

  @media ${until(Device.TabletLarge)} {
    gap: 28px 10px;
  }

  &[data-component-theme='${1 /* HomeStepper */}'] {
    --sectionMargin: 50px;
    background-color: var(--bgColor);

    ${() => Title}, ${MediaCardStyles.Title} {
      ${fonts.matter.medium}
      color: var(--headingColor);
    }

    ${MediaCardStyles.Summary} {
      font-size: 18px;
      line-height: 26px;
    }
  }

  &[data-theme='sage1'] {
    --bgColor: ${brand.sage.sageDark};
    --headingColor: ${brand.white};
    --textColor: ${brand.white};

    &[data-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.homeStepper.white};
      --textColor: ${brand.homeStepper.white};
    }
  }

  &[data-theme='sage3'] {
    --bgColor: ${brand.sage.sageLight};
    --headingColor: ${brand.white};
    --textColor: ${brand.white};

    &[data-component-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.homeStepper.white};
      --textColor: ${brand.homeStepper.white};
    }
  }

  [data-theme='sunshine3'] & {
    --bgColor: ${brand.sunshine.sunshineDark};
  }

  &[data-theme='plum3'] {
    --bgColor: ${brand.plum.plumLight};

    &[data-component-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.plum.plumDark};
      --textColor: ${brand.plum.plumDark};
    }
  }

  &[data-theme='toast1'] {
    --bgColor: ${brand.toast.toastLight};
  }

  &[data-theme='toast2'] {
    --bgColor: ${brand.toast.toastMedium};

    &[data-component-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.toast.toastBurnt};
      --textColor: ${brand.toast.toastBurnt};
    }
  }

  &[data-theme='grey'] {
    --bgColor: ${brand.grey.greyLight};

    &[data-component-theme='${1 /* HomeStepper */}'] {
      --headingColor: ${brand.sage.sageDark};
      --textColor: ${brand.homeStepper.black};
    }
  }
`;
const Title = styled.h2 `
  ${HeadingStyles.H2Styles}
  margin: 0 0 15px;
  max-width: 720px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
  transition-delay: 0.3s;

  @media (prefers-reduced-motion: reduce) {
    transform: translateY(0);
    transition: opacity 1s ${animation.secondary};
  }

  [data-is-active='true'] & {
    opacity: 1;
    transform: translateY(0);
  }

  @media ${from(Device.Tablet)} {
    margin: 0 0 32px;
  }
`;
const Summary = styled.p `
  ${ParagraphStyles.LargeStyles}
  margin: 0 0 24px;
  max-width: 860px;
  font-size: 16px;
  line-height: 24px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
  transition-delay: 0.4s;

  @media (prefers-reduced-motion: reduce) {
    transform: translateY(0);
    transition: opacity 1s ${animation.secondary};
  }

  [data-component-theme='${1 /* HomeStepper */}'] & {
    ${fonts.matter.medium};
    color: var(--textColor);
  }

  [data-is-active='true'] & {
    opacity: 1;
    transform: translateY(0);
  }

  @media ${from(Device.Tablet)} {
    margin: 0 0 32px;
    font-size: 18px;
    line-height: 28px;
  }

  @media ${from(Device.TabletLarge)} {
    font-size: 20px;
    line-height: 30px;
  }

  @media ${from(Device.Desktop)} {
    font-size: 22px;
    line-height: 32px;
  }
`;
const CtaWrapper = styled.div `
  margin: -8px -8px 42px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
  transition-delay: 0.5s;

  @media (prefers-reduced-motion: reduce) {
    transform: translateY(0);
    transition: opacity 1s ${animation.secondary};
  }

  [data-is-active='true'] & {
    opacity: 1;
    transform: translateY(0);
  }

  ${ButtonStyles.Button} {
    margin: 8px;
  }

  @media ${from(Device.Tablet)} {
    grid-row: 3;
    grid-column: 1 / span 8;
    margin: -10px -10px 70px;

    ${ButtonStyles.Button} {
      margin: 10px;
    }
  }

  @media ${until(Device.Tablet)} {
    grid-row: 4;
    grid-column: 1 / span 12;
  }
`;
const ItemList = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;

  [data-component-theme]:not([data-component-theme='${1 /* HomeStepper */}']) &:before {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -25%;
    width: 150%;
    height: calc(100% - 10px);
    background-color: var(--bgColor);
  }

  margin-bottom: -30px;

  @media ${from(Device.MobileLarge)} {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0 -10px -40px;
  }

  @media ${from(Device.TabletLarge)} {
    margin: 0 -10px;
  }

  @media ${from(Device.Desktop)} {
    margin: 0 -20px;
  }
`;
const Item = styled.div `
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
  transition-delay: 0.7s;
  max-width: 900px;

  @media (prefers-reduced-motion: reduce) {
    transform: translateY(0);
    transition: opacity 1s ${animation.secondary};
  }

  [data-is-active='true'] & {
    opacity: 1;
    transform: translateY(0);
  }

  &:nth-child(2) {
    transition-delay: 0.8s;
  }
  &:nth-child(3) {
    transition-delay: 0.9s;
  }
  &:nth-child(4) {
    transition-delay: 1s;
  }
  @media ${until(Device.MobileLarge)} {
    margin: 0 0 30px;
  }

  @media ${from(Device.MobileLarge)} {
    flex: 1 0 calc(100% - 40px);
    margin: 0 20px 40px;
  }

  @media ${from(Device.TabletLarge)} {
    flex: 1 0 calc(25% - 20px);
    margin: 0 10px;
  }

  @media ${from(Device.DesktopSmall)} {
    flex: 1 0 calc(25% - 40px);
    margin: 0 20px;
  }
`;
const Image = styled.img `
  display: block;
  object-fit: cover;
  object-position: center;
  width: 100%;

  max-height: 700px;

  [data-cropped-image='true'] & {
    height: auto;
  }

  [data-cropped-image='false'] & {
    object-fit: contain;
    width: auto;
    margin: auto;
  }

  @media ${until(Device.Tablet)} {
    max-height: 400px;
  }

  @media ${until(Device.MobileLarge)} {
    max-height: 260px;
  }
`;
const Inner = styled.div `
  display: grid;
  gap: 40px;
  grid-template-columns: repeat(12, 1fr);

  @media ${until(Device.TabletLarge)} {
    gap: 28px 10px;
  }
`;
const TitleWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    grid-row: 1;
    grid-column: 1 / span 12;
  }

  @media ${from(Device.Tablet)} {
    grid-row: 1;
    grid-column: 1 / span 8;
  }
`;
const SummaryWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    grid-row: 2;
    grid-column: 1 / span 12;
  }

  @media ${from(Device.Tablet)} {
    grid-row: 2;
    grid-column: 1 / span 8;
  }
`;
const ImageWrapper = styled.div `  
  @media ${until(Device.Tablet)} {
    grid-row: 3;
    grid-column: 1 / span 12;
  }

  @media ${from(Device.Tablet)} {
    grid-row: 1 / span 3;
    grid-column: 8 / span 4;
  }
  margin: 0 0 70px;
`;
const CtaCardWidgetStyles = {
    Container,
    CtaWrapper,
    Image,
    ImageWrapper,
    Inner,
    Item,
    ItemList,
    Summary,
    SummaryWrapper,
    Title,
    TitleWrapper,
};
export default CtaCardWidgetStyles;
